import { PaginatedResponse } from "@/interfaces/paginatedResponse";
import { Hotel } from "@/internal/modules/providers/interfaces/Hotel";
import { CustomAxiosRequestConfig } from "@/interfaces/customAxiosRequestConfig";
import axios from "@/lib/axios";
import { useQuery } from "@tanstack/vue-query";
import { MaybeRef, get } from "@vueuse/core";
import { computed } from "vue";

interface Props {
  immediate?: boolean;
  params?: Params;
  config?: Config;
}

interface Config {
  errorHandling?: boolean;
}

interface Params {
  userId?: MaybeRef<string | number | undefined>;
  page?: MaybeRef<number | undefined>;
  limit?: MaybeRef<number | undefined>;
  sortBy?: MaybeRef<string | undefined>;
  sortOrder?: MaybeRef<'asc' | 'desc' | undefined>;
}

const getUserFavoriteHotels = async (
  params?: Params,
  errorHandling = false
): Promise<PaginatedResponse<Hotel>> => {
  const userId = get(params?.userId);
  if (!userId) {
    throw new Error('User ID is required');
  }

  const queryParams = new URLSearchParams();
  
  const page = get(params?.page);
  const limit = get(params?.limit);
  const sortBy = get(params?.sortBy);
  const sortOrder = get(params?.sortOrder);
  
  if (page) queryParams.append('page', page.toString());
  if (limit) queryParams.append('limit', limit.toString());
  if (sortBy) queryParams.append('sort_by', sortBy);
  if (sortOrder) queryParams.append('sort_order', sortOrder);

  const queryString = queryParams.toString();
  const url = `/api/user/favorites/hotels${queryString ? `?${queryString}` : ''}`;

  const { data } = await axios.get<PaginatedResponse<Hotel>>(
    url,
    { errorHandling } as CustomAxiosRequestConfig
  );

  return data;
};

const useUserFavoriteHotels = (props?: Props) => {
  const { immediate = true, params, config } = props || {};
  
  const { data, isLoading, refetch, isRefetching, isFetching, error } = useQuery(
    ["userFavoriteHotels"],
    () => getUserFavoriteHotels(params!, config?.errorHandling || false),
    {
      enabled: immediate && !!get(params?.userId),
      retry: false,
    }
  );

  return {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
    isRefetching
  };
};

export default useUserFavoriteHotels;
